import React, { useState, useEffect, useMemo } from 'react';
import { Button, 
    TableCell, 
    TableHead, 
    TableRow, 
    TextField,
    Select, 
    MenuItem, 
    Dialog, 
    DialogContent, 
    DialogContentText, 
    DialogActions, 
    TableBody, 
    Box, 
    DialogTitle, 
    Stack,
    Typography,
    TableContainer
} from '@mui/material';
import { usePost, usePostFile } from '../../../apis/ApiRequests';
import { fileURL } from '../../../apis/ApiConfig';
import Header from '../../../components/uiParts/hygiene/Header';
import { useAuth } from '../../../contexts/AuthContext';
import '../../../App.css';
import { CustomTable } from '../../../components/table/CustomTable';
import CustomSnackBar from '../../../components/alert/CustomSnackBar';
import CheckStatusButtons from '../../../components/input/CheckStatusButtons';
import DeviceMonitoringTable from '../../../components/table/DeviceMonitoringTable';
import { generateDailyReportPDF } from '../exportReportPdf';
import TitleBar from '../../../components/uiParts/TitleBar';
import CloseIcon from "@mui/icons-material/Close";

const CheckList = () => {
    //===============================================
    // initialize
    //===============================================
    //-----------------------------------------------
    // initialize - 基本情報を設定
    //-----------------------------------------------
    const { authInfo }   = useAuth();
    
    const facilityCode   = authInfo.facilityCode;
    const facilityName   = authInfo.facilityName;
    const userId         = authInfo.userId;
    const userName       = authInfo.userName
	const authority      = Number(authInfo.authority);
    const today          = authInfo.targetDate;

    const [ entryDate,         setEntryDate        ] = useState(today);
    const [ requireMessage,    setRequireMessage   ] = useState('');
    const [ isLoading,         setIsLoading        ] = useState(false);
    const [ haccpMasta,        setHaccpMasta       ] = useState([]);
    const [ checkResults,      setCheckResults     ] = useState([]);
    const [ pastCheckedData,   setPastCheckedData  ] = useState([]);
    const [ reportTimeList,    setReportTimeList   ] = useState({});
    const [ hygieneId,         setHygieneId        ] = useState(0);
    const [ deleteHygieneId,   setDeleteHygieneId  ] = useState(null); 
    const [ approveHygieneId,  setApproveHygieneId ] = useState(null); 
    const [ editableData,      setEditableData     ] = useState([]);
    const [ monitoringData,    setMonitoringData   ] = useState([]);
    const [ reportOrder,       setReportOrder      ] = useState([]);
    const [ approvedMap,       setApprovedMap      ] = useState({});
	const [ detailOpen,        setDetailOpen       ] = useState(false);
    const [ detailCode,        setDetailCode       ] = useState(null);
    const [ detailReportId,    setDetailReportId   ] = useState(null);
    const [ openDialogImageFile,   setOpenDialogImageFile   ] = useState(false);
    const isPast = detailReportId !== null && detailReportId !== 0;
    const detailData = isPast
        ? pastCheckedData?.[detailCode]?.[detailReportId]
        : checkResults?.[detailCode];
    const isDetailReadOnly =
        approvedMap?.[detailReportId ?? hygieneId] === true;
    const LIMIT_LIST = [1, 2, 3].map(id => ({
        deviceId: id,
        lowerLimit: -100,
        upperLimit: 100,
        unit: '℃',
    }));

    // UI Message
    const [messageState, setMessageState] = useState({
        snackBarTrue: false,
        snackBarFalse: false,
        confirm: false,
        require: false,
        snackBarDeleteTrue: false,
        snackBarDeleteFalse: false,
        confirmDelete: false,
        confirmApproval: false,
    });
    
    // コメントの表示制限
    const MAX_COMMENT_LEN = 45;

    //===============================================
    // API
    //===============================================
	const { postFile, loading, error, response:postFile_Res } = usePostFile();
    //-----------------------------------------------
    // API - searchData / Haccpマスター情報を取得する
    //-----------------------------------------------
    const { response : searchData_Res, loading : searchData_Loading, post:searchData_Post } = usePost();
    const searchHaccpData = async () => {
        const postData = {
            'className'    : 'mch.models.masta.Haccp',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
        }
        await searchData_Post(postData);
    };
    //-----------------------------------------------
    // API - 日報データを取得する
    //-----------------------------------------------
    const { response: getReportData_res, post: getReportData_post } = usePost();
    const getReportData = async () => {
        const postData = {
            'className'    : 'mch.models.table.Hygiene',
            'methodName'   : 'getReportDataByDate',
            'facilityCode' : facilityCode,
            'entryDate'    : entryDate,
            'hygieneId'    : hygieneId,
        }
        await getReportData_post(postData);
    }
    //-----------------------------------------------
    // API - 変更情報を保存する
    //-----------------------------------------------
    const { response: saveHygiene_res, post: saveHygiene_post } = usePost();
    const saveCheckData = async () => {
        const postData = {
            'className'     : 'mch.models.table.Hygiene',
            'methodName'    : 'saveHygiene',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'checkedList'   : { ...checkResults },
            'hygieneId'     : hygieneId,
            'entryDate'     : entryDate,
            'sensorDataList': editableData,
        }
        await saveHygiene_post(postData)
    }
    //-----------------------------------------------
    // API - 変更情報を保存する 過去
    //-----------------------------------------------
    const { response: savePastData_res, post: savePastData_post } = usePost();
    const savePastData = async () => {
        const postData = {
            'className'     : 'mch.models.table.Hygiene',
            'methodName'    : 'savePastData',
            'facilityCode'  : facilityCode,
            'userId'        : userId,
            'checkedList'   : { ...pastCheckedData },
            // 'hygieneId'     : hygieneId,  hygieneId はIRIS
            'entryDate'     : entryDate,
        }
        await savePastData_post(postData)
    }
    //-----------------------------------------------
    // API - 日報データを削除する
    //-----------------------------------------------
    const { response: deleteHygiene_res, post: deleteHygiene_post } = usePost();
    const deleteHygiene = async (hyId) => {
        const postData = {
            'className'    : 'mch.models.table.Hygiene',
            'methodName'   : 'deleteHygiene',
            'facilityCode' : facilityCode,
            'hygieneId'    : hyId,
        }
        await deleteHygiene_post(postData);
    }
    //-----------------------------------------------
    // API - 日報データを承認する
    //-----------------------------------------------
    const { response: approveHygiene_res, post: approveHygiene_post } = usePost();
    const approveHygiene = async (hyId) => {
        const postData = {
            'className'    : 'mch.models.table.Hygiene',
            'methodName'   : 'approveHygiene',
            'facilityCode' : facilityCode,
            'hygieneId'    : hyId,
        }
        await approveHygiene_post(postData);
    }
    //-----------------------------------------------
    // Function - 今日の日付かの判定 
    //-----------------------------------------------
    const isToday = (dateStr) => {
        const todayData = new Date().toISOString().split('T')[0];
        return dateStr === todayData;
    };
    //-----------------------------------------------
    // 編集可否の判定
    //-----------------------------------------------
    const canEdit =
        !(hygieneId !== 0 && approvedMap[hygieneId]);
	//===============================================
	// useEffect
	//===============================================
    //-----------------------------------------------
    // useEffect - ロード時の設定
    //-----------------------------------------------
    useEffect(() => {
        const fetchData = async () => {
            await searchHaccpData();
            await getReportData();
        };
        fetchData();
    }, []);
	//-----------------------------------------------
    // useEffect - searchData / haccp
	//-----------------------------------------------
    useEffect(() => {
        setHaccpMasta([]);
        if (searchData_Res) {
            setHaccpMasta(searchData_Res.Data);
        }
    }, [searchData_Res]);
    //-----------------------------------------------
    // useEffect - モニタリングデータ取得
    //-----------------------------------------------
    useEffect(() => {
        if (!getReportData_res?.dailyReport) return;

        const sortedHygiene = sortHygieneById([...getReportData_res.dailyReport])
        const monitoringFlatList = sortedHygiene.flatMap(report =>
                    (report.monitoringData || []).map(m => ({
                        ...m,
                        hygieneId: report.hygieneId,
                        entryTime: report.entryTime,
                        entryDate: report.entryDate,
                        userName: report.userName,
                    }))
                );

        setMonitoringData(monitoringFlatList);
    }, [getReportData_res]);
    //-----------------------------------------------
    // useEffect - チェックデータ初期値("NONE")を設定
    //-----------------------------------------------
    useEffect(() => {
        if (haccpMasta.length > 0) {
            setCheckResults(initCheckedDataFromMaster(haccpMasta));
        }
    }, [haccpMasta]);
    //-----------------------------------------------
    // useEffect - 日付変更時の初期化 + データ取得
    //-----------------------------------------------
    useEffect(() => {
        if (!entryDate) return;

        // 初期化
        setCheckResults({});
        setPastCheckedData({});
        setReportOrder([]);
        setReportTimeList({});
        setMonitoringData([]);
        setHygieneId(0);
        setEditableData([]);

        // データ取得
        getReportData();

    }, [entryDate]);

    //-----------------------------------------------
    // useEffect - reportOrder 更新時に hygieneId を調整
    //-----------------------------------------------
    useEffect(() => {
        if (reportOrder.length === 0) return;

        const isInvalidId =
            hygieneId === 0 || !reportOrder.includes(hygieneId);

        if (!isToday(entryDate) && isInvalidId) {

            const unapprovedId = reportOrder.find(id => !approvedMap[id]);

            if (unapprovedId) {
                // 未承認のIDがあればそれを設定
                setHygieneId(unapprovedId);
            } else {
                // 全て承認済みの場合は最新IDを設定（閲覧のみ）
                setHygieneId(reportOrder[0]);
            }
        }
    }, [
        reportOrder,
        approvedMap,
        hygieneId,
        entryDate
    ]);
    //-----------------------------------------------
    // useEffect - 編集対象 editableData の生成
    //-----------------------------------------------
    useEffect(() => {
        if (!canEdit) {
            setEditableData([]);
            return;
        }

        // 新規登録
        if (hygieneId === 0) {
            setEditableData(createInitialSensorData());
            return;
        }

        // 既存データ編集
        if (monitoringData && monitoringData.length > 0) {
            setEditableData(
                monitoringData
                    .filter(item => item.hygieneId == hygieneId)
                    .map(item => ({ ...item }))
            );
        }
    }, [hygieneId, monitoringData, canEdit]);
    //-----------------------------------------------
    // useEffect - reportOrderの反映
    //-----------------------------------------------
    useEffect(() => {
        if (!getReportData_res ||!Array.isArray(getReportData_res.dailyReport)) {
            return;
        }

        const sortedHygiene = sortHygieneById([...getReportData_res.dailyReport])
        const reportOrder = sortedHygiene.map(r => r.hygieneId);
        setReportOrder(reportOrder);

    }, [getReportData_res]);
    //-----------------------------------------------
    // useEffect - チェックデータの反映
    //-----------------------------------------------
    useEffect(() => {
        if (!getReportData_res || !Array.isArray(getReportData_res.dailyReport)) {
            return;
        }

        const newTimeList = {};
        const newApprovedMap = {};
        const normalizedPast = {};

        // haccpMasta から初期状態を作る
        const newCheckedData = initCheckedDataFromMaster(haccpMasta);

        getReportData_res.dailyReport.forEach((report) => {
            if (!report) return;

            newTimeList[report.hygieneId] = report.entryTime;
            newApprovedMap[report.hygieneId] = report.approvedFlag === 1;
            // reportChecked を処理
            (report.reportChecked || []).forEach((checkItem) => {
                const cid = checkItem.code;
                const status = checkItem.status || "NONE";

                // 備考 textx1～5
                const comments = [
                    checkItem.textx1,
                    checkItem.textx2,
                    checkItem.textx3,
                    checkItem.textx4,
                    checkItem.textx5,
                ].filter(v => typeof v === "string" && v.trim() !== "");

                // 画像 imagePath1～5
                const imagePaths = [
                    checkItem.imagePath1,
                    checkItem.imagePath2,
                    checkItem.imagePath3,
                    checkItem.imagePath4,
                    checkItem.imagePath5,
                ]
                    .filter(v => typeof v === "string" && v.trim() !== "")
                    .map(path => ({ path }));

                // 過去履歴用（statusだけ）
                if (!normalizedPast[cid]) {
                    normalizedPast[cid] = {};
                }

                normalizedPast[cid][report.hygieneId] = {
                    status,
                    comments,
                    imagePaths,
                    name: checkItem.name ?? null,
                };

                // 今選択中の hygieneId のときだけ詳細をセット
                if (report.hygieneId === hygieneId) {
                    newCheckedData[cid] = {
                        status,
                        comment: comments,
                        imagePaths,
                        code: checkItem.code ?? null,
                        name: checkItem.name ?? null,
                    };
                }
            });
        });

        setReportTimeList(newTimeList);
        setCheckResults(newCheckedData);
        setPastCheckedData(normalizedPast);
        setApprovedMap(newApprovedMap);

    }, [getReportData_res, hygieneId, haccpMasta]);
    //-----------------------------------------------
    // useEffect - 保存APIレスポンス処理
    //-----------------------------------------------
    useEffect(() => {
        processApiResponse({
            response: saveHygiene_res,
            onSuccess: () => {
                setMessageInfo('snackBarTrue', true); // 保存しました
            },
            onErrorMessage: (res) =>
                `保存に失敗しました。`
        });
    }, [saveHygiene_res]);
    //-----------------------------------------------
    // useEffect - 削除APIレスポンス処理
    //-----------------------------------------------
    useEffect(() => {
        processApiResponse({
            response: deleteHygiene_res,
            onSuccess: () => {
                setMessageInfo('snackBarDeleteTrue', true); // 削除しました
            },
            onErrorMessage: (res) =>
                `削除に失敗しました。`
        });
    }, [deleteHygiene_res]);
    //-----------------------------------------------
    // useEffect - 承認APIレスポンス処理
    //-----------------------------------------------
    useEffect(() => {
        processApiResponse({
            response: approveHygiene_res,
            onSuccess: () => {
                setMessageInfo('snackBarApproveTrue', true); // 承認しました
            },
            onErrorMessage: (res) =>
                `承認に失敗しました。`
        });
    }, [approveHygiene_res]);
    //===============================================
    // eventHandler
    //===============================================
    //-----------------------------------------------
    // eventHandler - 日付の変更
    //-----------------------------------------------
    const handleChange_entryDate = (value) => {
        setEntryDate(value);
        setHygieneId(0);
    };
    //-----------------------------------------------
    // eventHandler - チェックボックス変更がある時のイベント
    //-----------------------------------------------
    const handleChange_reportCheck = (item, status) => {
        if (!canEdit) return;

        setCheckResults(prev => {
            const cur = prev[item.code] ?? {
                status: 'NONE',
                comment: [],
                imagePaths: [],
                name: item.name ,
                code: item.code,
            };

            const nextStatus = cur.status === status ? 'NONE' : status;

            return {
                ...prev,
                [item.code]: {
                    ...cur,
                    status: nextStatus,
                },
            };
        });
        if (status === 'NG') {
            handleButton_openDetail(item.code);
        }
    };
    //-----------------------------------------------
    // eventHandler - 登録ボタン押下
    //-----------------------------------------------
    const handleButton_register = () => {
        setIsLoading(true);
        if (!validateData()) return; //バリデーション
        setMessageInfo('confirm', true);
    }
    //-----------------------------------------------
    // eventHandler - (登録) ダイアログの続行ボタン押下
    //-----------------------------------------------
    const handleButton_saveAction = async () => {
        await saveCheckData();
        await savePastData();
        setHygieneId(0);
    }
    //-----------------------------------------------
    // eventHandler - 削除ボタン押下
    //-----------------------------------------------
    const handleButton_deleteReport = (repId) => {
        setDeleteHygieneId(repId);
        setMessageInfo('confirmDelete', true);
    }
    //-----------------------------------------------
    // eventHandler - (削除) ダイアログの続行ボタン押下 
    //-----------------------------------------------
    const handleButton_deleteAction = async () => {
        await deleteHygiene(deleteHygieneId);
        setDeleteHygieneId(null);
    }
    //-----------------------------------------------
    // eventHandler - 承認ボタン押下
    //-----------------------------------------------
    const handleButton_approveReport = (repId) => {
        setApproveHygieneId(repId);
        setMessageInfo('confirmApproval', true);
    }
    //-----------------------------------------------
    // eventHandler - (承認) ダイアログの続行ボタン押下 
    //-----------------------------------------------
    const handleButton_approveAction = async () => {
        await approveHygiene(approveHygieneId);
        setApproveHygieneId(null);
        setHygieneId(0);
    }
    //-----------------------------------------------
    // eventHandler - 詳細Dialogを表示
    //-----------------------------------------------
    function handleButton_openDetail(haccpCode, reportId = null) {
        setDetailCode(haccpCode);

        // 表示対象のIDを決定
        const targetId =
            (reportId !== null && reportId !== undefined)
                ? reportId
                : hygieneId;

        setDetailReportId(targetId);

        setDetailOpen(true);
    }
    //-----------------------------------------------
    // eventHandler - 詳細Dialogを閉じる
    //-----------------------------------------------
	function handleButton_closeDetail() {
		setDetailOpen(false);
        setDetailCode(null);
	}
    //-----------------------------------------------
    // eventHandler - センサーデータ変更
    //-----------------------------------------------
    const handleChange_editableData = (index, field, value) => {
        setEditableData(prev => {
            const newData = [...(prev || [])];
            newData[index] = {
                ...newData[index],
                [field]: value
            };
            return newData;
        });
    };
     //-----------------------------------------------
    // eventHandler - PDF書き出し
    //-----------------------------------------------
    const handlePdfOutput = () => {
        generateDailyReportPDF({
            entryDate,
            userName,
            haccpMasta,
            reportOrder,
            reportTimeList,
            pastCheckedData,
            monitoringData,
        });
    };

	//-----------------------------------------------
    // eventHandler - 画像ファイルのダイアログ表示
	//-----------------------------------------------
    const handleClick_dialog_imageFile = (src) => {
        setOpenDialogImageFile(src);
    };
	//-----------------------------------------------
    // eventHandler - 画像ファイルのダイアログ非表示
	//-----------------------------------------------
    const handleClick_dialog_imageFile_close = () => {
        setOpenDialogImageFile(false);
    };
	//===============================================
	// UI Message - スナックバーのメッセージ設定
	//===============================================
    const setMessageInfo = (target, state) => {
        setMessageState(prevSearchData => ({
            ...prevSearchData,
            [target]: state,
        }));
    };
    //===============================================
    // Function
    //===============================================
    //-----------------------------------------------
    // Function - バリデーション、範囲チェック
    //-----------------------------------------------
    const validateData = () => {
        let messages = [];

        const isInvalid = (v) =>
            v === "" || v === null || isNaN(v) || Number(v) < -1000 || Number(v) > 1000;

        editableData.forEach(item => {
            const { deviceId, value } = item;

            if (isInvalid(value)) {
                messages.push(`冷蔵庫 ${deviceId}：温度の値が不正です（-1000～1000）`);
            }
        });

        if (messages.length > 0) {
            setRequireMessage(messages.join(" / "));
            setMessageInfo("require", true);
            return false;
        }

        setIsLoading(false);
        return true;
    };

    //-----------------------------------------------
    // すべて承認済みかの判定
    //-----------------------------------------------
    const allApproved = useMemo(() => {
        return reportOrder.length > 0 && reportOrder.every(id => approvedMap[id]);
    }, [reportOrder, approvedMap]);
    //-----------------------------------------------
    // 詳細表示用：選択中HACCP項目（マスタ参照）
    //-----------------------------------------------
    const selectedHaccpItem  = useMemo(() => {
        if (detailCode == null) return null;
        return haccpMasta.find(r => r.code  === detailCode) || null;
    }, [detailCode, haccpMasta]);
    //-----------------------------------------------
    // Function - 初期値作成
    //-----------------------------------------------
    const createInitialSensorData = () =>
    Array.from({ length: 3 }, (_, i) => ({
        deviceId: i + 1,
        value: 0
    }));
    //-----------------------------------------------
    // Function - APIレスポンス共通処理
    //-----------------------------------------------
    const processApiResponse = async ({
        response,
        onSuccess,
        onErrorMessage
    }) => {
        if (!response) return;

        if (response.result !== 'NG') {
            await getReportData();

            setMessageInfo('require', false);
            setMessageInfo('confirm', false);
            setMessageInfo('confirmDelete', false);
            setMessageInfo('confirmApproval', false);

            onSuccess?.();
        } else {
            setRequireMessage(onErrorMessage(response));
            setMessageInfo('require', true);
            setMessageInfo('confirm', false);
            setMessageInfo('confirmDelete', false);
            setMessageInfo('confirmApproval', false);
        }

        setIsLoading(false);
    };
    //-----------------------------------------------
    // Function - 時間帯表記
    //-----------------------------------------------
    const getReportTimeLabel = ({
        hygieneId,
        reportTimeList,
        approvedMap,
        allApproved = false,
    }) => {
        // 全て承認済みの場合は非表示
        if (allApproved) return null;

        const isApproved = approvedMap?.[hygieneId];
        const timeLabel = formatTime(reportTimeList?.[hygieneId]) || '';

        if (hygieneId === 0) return '新規登録';
        if (!timeLabel) return '';

        return isApproved ? (
            <>
                <div style={{ color: '#305496', fontWeight: 600 }}>
                    {timeLabel}
                </div>
            </>
        ) : (
            timeLabel
        );
    };
    //-----------------------------------------------
    // Function - 画像追加（File保持）
    //-----------------------------------------------
    async function addImages(fileList) {

        if (detailCode === null) return;

        const isPast = detailReportId !== null && detailReportId !== 0;;

        const currentImages = isPast
            ? (pastCheckedData?.[detailCode]?.[detailReportId]?.imagePaths ?? [])
            : (checkResults?.[detailCode]?.imagePaths ?? []);

        if (currentImages.length >= 5) {
            alert("画像は最大5件までです。");
            return;
        }

        const file = fileList[0];
        const ext = file.name.split('.').pop().toLowerCase();
        const safeName = `${Date.now()}.${ext}`;

        const charFromCode1 = String.fromCharCode(1);
        const newUserCodePrefix = `${facilityCode}${charFromCode1}${userId}${charFromCode1}`;
        const newFileName = newUserCodePrefix + safeName;

        const newFile = new File([file], newFileName, { type: file.type });

        const arg = new FormData();
        arg.append('targetFile', newFile);

        const res = await postFile(arg);
        if (!res) return;

        // ===== 分岐 =====
        if (isPast) {
            // 過去
            setPastCheckedData(prev => {
                const codeBlock = prev[detailCode] ?? {};
                const cur = codeBlock[detailReportId] ?? {
                    status: 'NONE',
                    comments: [],
                    imagePaths: [],
                };

                return {
                    ...prev,
                    [detailCode]: {
                        ...codeBlock,
                        [detailReportId]: {
                            ...cur,
                            imagePaths: [
                                ...(cur.imagePaths ?? []),
                                {
                                    path: res,
                                    file: newFile,
                                }
                            ]
                        }
                    }
                };
            });

        } else {
            // 現在 or 新規
            setCheckResults(prev => {
                const cur = prev[detailCode] ?? {
                    status: 'NONE',
                    comment: [],
                    imagePaths: [],
                    code: null,
                    name: '',
                };

                return {
                    ...prev,
                    [detailCode]: {
                        ...cur,
                        imagePaths: [
                            ...cur.imagePaths,
                            {
                                path: res,
                                file: newFile,
                            }
                        ]
                    }
                };
            });
        }
    }
    //-----------------------------------------------
    // Function - 画像削除
    //-----------------------------------------------
    function removeImage(imgIdx) {

        if (detailCode === null) return;

        const isPast = detailReportId !== null && detailReportId !== 0;

        if (isPast) {
            // 過去
            setPastCheckedData(prev => {
                const codeBlock = prev[detailCode] ?? {};
                const cur = codeBlock[detailReportId];

                if (!cur || !cur.imagePaths) return prev;

                const target = cur.imagePaths[imgIdx];

                if (target?.file) {
                    URL.revokeObjectURL(target.file);
                }

                const newImages = cur.imagePaths.filter((_, i) => i !== imgIdx);

                return {
                    ...prev,
                    [detailCode]: {
                        ...codeBlock,
                        [detailReportId]: {
                            ...cur,
                            imagePaths: newImages,
                        }
                    }
                };
            });

        } else {
            // 現在 or 新規
            setCheckResults(prev => {
                const cur = prev[detailCode];
                if (!cur || !cur.imagePaths) return prev;

                const target = cur.imagePaths[imgIdx];

                if (target?.file) {
                    URL.revokeObjectURL(target.file);
                }

                const newImages = cur.imagePaths.filter((_, i) => i !== imgIdx);

                return {
                    ...prev,
                    [detailCode]: {
                        ...cur,
                        imagePaths: newImages,
                    },
                };
            });
        }
    }
    //-----------------------------------------------
    // Function - 詳細更新（備考）
    //-----------------------------------------------
    function updateDetail(index, value) {
        if (detailCode == null) return;

        const isPast = detailReportId !== null && detailReportId !== 0;

        if (isPast) {
            // 過去だけ更新
            setPastCheckedData(prev => {
                const codeBlock = prev[detailCode] ?? {};
                const target = codeBlock[detailReportId] ?? {};

                const prevComments = Array.isArray(target.comments)
                    ? [...target.comments]
                    : [];

                prevComments[index] = value;

                return {
                    ...prev,
                    [detailCode]: {
                        ...codeBlock,
                        [detailReportId]: {
                            ...target,
                            comments: prevComments,
                        }
                    }
                };
            });

        } else {
            // 現在だけ更新
            setCheckResults(prev => {
                const current = prev[detailCode] ?? {};
                const prevComments = Array.isArray(current.comment)
                    ? [...current.comment]
                    : [];

                prevComments[index] = value;

                return {
                    ...prev,
                    [detailCode]: {
                        ...current,
                        comment: prevComments,
                    }
                };
            });
        }
    }
    //-----------------------------------------------
    // Function - Haccpチェック項目取得後にチェックデータの初期値を設定
    //-----------------------------------------------
    const initCheckedDataFromMaster = (haccpMaster) => {
        const init = {};

        haccpMaster.forEach(item => {
            init[item.code] = {
                status: 'NONE',
                comment: [],
                imagePaths: [],
                code: item.code ?? null,
                name: item.name ?? null,
            };
        });

        return init;
    };
    //-----------------------------------------------
    // Function - 過去データの表示
    //-----------------------------------------------
    const CheckStatusView = ( status ) => {
        if (status === 'OK') return '〇';
        if (status === 'NG') return '×';
        return '';
    };
    //-----------------------------------------------
    // Function - 時間変換
    //-----------------------------------------------
    const formatTime = (time) => {
        if (time === null || time === undefined) return "";
        const t = String(time).padStart(6, "0");
        return `${t.substring(0, 2)}:${t.substring(2, 4)}`;
    };
    //-----------------------------------------------
    // Function - hygieneId を並び替え
    //-----------------------------------------------
    const sortHygieneById = (Hygiene = []) => {
        return [...Hygiene].sort((a, b) => a.hygieneId - b.hygieneId);
    };
    //===============================================
    // Css
    //===============================================
    const dialog_button_close = { 
        fontSize: '16px', height:'40px', width:'690px', color:'#fff', backgroundColor:'#808080'
    };
    
    //===============================================
    // JSXのレンダー
	//===============================================

    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/hygineList" setEntryDate={handleChange_entryDate} />
            <div className='bodyContens'>
                <TitleBar title="高度衛生管理チェック" color="#d66a35" />
                <div style={{ margin: '10px', padding: '10px' }} className='mainContents'>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: "10px",
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {canEdit && (
                                <Button
                                    color="tertiary"
                                    size="large"
                                    variant="contained"
                                    style={{
                                        color: '#fff',
                                        width: '270px',
                                        backgroundColor: '#d66a35',
                                        fontSize: '18px'
                                    }}
                                    onClick={handleButton_register}
                                >
                                    登録
                                </Button>
                            )}
                            <Button style={{ height: '40px', marginLeft: '10px', }} variant="outlined" onClick={handlePdfOutput}>
                                PDF出力
                            </Button>
                        </div>
                        {/* <div style={{ display: 'flex' }}>
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                編集時間選択：
                            </span>
                            <Select
                                size="small"
                                sx={{
                                    '& .MuiSelect-select': {
                                        padding: '4px 8px'
                                    }
                                }}
                                value={
                                    (hygieneId === 0 || reportOrder.includes(hygieneId)) ? hygieneId : ""
                                }
                                onChange={e => setHygieneId(Number(e.target.value))}
                            >
                                <MenuItem value={0}>【新規登録】</MenuItem>
                                {Object.entries(reportTimeList).map(([id, time]) => (
                                    <MenuItem
                                        key={id}
                                        value={Number(id)}
                                        disabled={approvedMap[id]}
                                    >{formatTime(time)}</MenuItem>
                                ))}
                            </Select>
                        </div> */}
                    </div>
                    {/* 高度衛生管理チェック */}
                    <TableContainer
                        sx={{
                            maxHeight: 570,
                            overflowY: "auto",
                            border: '1px solid #cfcfcf',
                            borderRadius: '4px',
                        }}
                    >
                        <CustomTable
                            style={{ marginBottom: '10px', tableLayout: 'fixed' }}
                            sx={{ '& tr td': { backgroundColor: '#fff' } }} // 交互色の打ち消し
                        >
                            {/* 時刻ヘッダー行 */}
                            <TableHead sx={{
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 2,
                            }}>
                                <TableRow>
                                    {/* 空のセル */}
                                    <TableCell sx={{
                                        backgroundColor: 'white !important',
                                        color: 'black !important',
                                        borderBottom: "none",
                                        borderColor: '#cfcfcf !important',
                                        borderRight: 'none !important',
                                        fontWeight: 700,
                                    }}>項目</TableCell>
                                    <TableCell sx={{
                                        backgroundColor: 'white !important',
                                        color: 'black !important',
                                        borderBottom: "none",
                                        borderColor: '#cfcfcf !important',
                                        width: '80px !important',
                                        borderRight: !canEdit ? 'none !important' : undefined,
                                    }}></TableCell>

                                    {reportOrder.map((repId, idx) => (
                                        <TableCell
                                            key={repId}
                                            sx={{
                                                backgroundColor: 'white !important',
                                                color: 'black !important',
                                                borderBottom: 'none',
                                                fontWeight: 'bold',
                                                borderColor: '#cfcfcf !important',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                width: '80px !important',
                                                padding: '6px',

                                            }}
                                        >
                                            {getReportTimeLabel({
                                                hygieneId: repId,
                                                reportTimeList,
                                                approvedMap,
                                            })}
                                        </TableCell>
                                    ))}
                                    <TableCell sx={{
                                        backgroundColor: 'white !important',
                                        color: 'black !important',
                                        borderBottom: 'none',
                                        borderColor: '#cfcfcf !important',
                                        textAlign: 'center',
                                        width: '120px !important',
                                        whiteSpace: 'pre-wrap',
                                        padding: '0px',
                                    }}>{getReportTimeLabel({
                                        hygieneId,
                                        reportTimeList,
                                        approvedMap,
                                        allApproved,
                                    })}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            {haccpMasta?.map((item) => {

                                const detail = checkResults[item.code];
                                const commentValue = detail?.comment ?? [];

                                const validComments = Array.isArray(commentValue)
                                    ? commentValue.filter(c => typeof c === "string" && c.trim() !== "")
                                    : [];

                                const hasComment = validComments.length > 0;

                                const joinedComment = validComments.join(" ");

                                const commentText = hasComment
                                    ? joinedComment.length > MAX_COMMENT_LEN
                                        ? `備考あり : ${joinedComment.slice(0, MAX_COMMENT_LEN)}...`
                                        : `備考あり : ${joinedComment}`
                                    : "";

                                return (
                                    <React.Fragment key={item.code}>
                                        <TableBody>
                                            <TableRow key={item.code} >
                                                <TableCell
                                                    sx={{
                                                        borderRight: 'none !important',
                                                        fontSize: '14px',
                                                    }}
                                                >
                                                    {item.name}
                                                    {(hasComment || detail?.imagePaths?.length > 0) && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ opacity: 0.7, display: 'block' }}
                                                        >
                                                            {commentText}
                                                            {commentText && detail?.imagePaths?.length > 0 ? ' / ' : ''}
                                                            {detail?.imagePaths?.length > 0
                                                                ? `画像:${detail.imagePaths.length}`
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    borderRight: !canEdit ? 'none !important' : undefined,
                                                }}
                                                >
                                                    {/* <Button
                                                        size="small"
                                                        variant="text"
                                                        onClick={() => handleButton_openDetail(item.code)}
                                                    >
                                                        詳細
                                                    </Button> */}
                                                </TableCell>

                                                {/* 過去データ */}
                                                {reportOrder.map((repId, idx) => {
                                                    
                                                    const status =
                                                        pastCheckedData?.[item.code]?.[repId]?.status ?? 'NONE';
                                                    const isClickable = ['NG', 'OK'].includes(status);
                                                    return (
                                                        <TableCell key={repId}
                                                            onClick={isClickable ? () => handleButton_openDetail(item.code, repId) : undefined}
                                                            sx={{
                                                                textAlign: 'center',
                                                                width: '80px !important',
                                                                padding: '8px',
                                                                cursor: isClickable ? 'pointer' : 'default',
                                                                '&:hover': isClickable
                                                                    ? { backgroundColor: '#f5f5f5' }
                                                                    : {},
                                                                '&:last-child': { borderRight: 'none' },
                                                                '& .MuiFormControlLabel-root': {
                                                                    margin: 0
                                                                }
                                                            }}
                                                        >
                                                            {CheckStatusView(status)}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell
                                                    sx={{
                                                        textAlign: 'center',
                                                        padding: '8px',
                                                        '&:last-child': { borderRight: 'none' },
                                                        '& .MuiFormControlLabel-root': {
                                                            margin: 0
                                                        },
                                                    }}
                                                >
                                                    <div style={{
                                                        display: 'inline-block',
                                                        transformOrigin: 'center center'
                                                    }}>
                                                        {canEdit && (
                                                            <CheckStatusButtons
                                                                value={checkResults?.[item.code]?.status ?? 'NONE'}
                                                                disabled={!canEdit}
                                                                onChange={(status) =>
                                                                    handleChange_reportCheck(item, status)
                                                                }
                                                            />
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                            {/* ===== 冷蔵・冷凍庫の温度チェック直下に差し込み ===== */}
                                            {item.code === 28 && (
                                                <TableRow >
                                                    <TableCell
                                                        colSpan={3 + reportOrder.length}
                                                        sx={{ padding: 0 }}
                                                    >
                                                        <TableContainer sx={{ width: '100%' }}>
                                                            <DeviceMonitoringTable
                                                                data={editableData}
                                                                monitoringData={monitoringData}
                                                                limitList={LIMIT_LIST}
                                                                canEdit={canEdit}
                                                                onEditableDataChange={handleChange_editableData}
                                                            />
                                                        </TableContainer>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </React.Fragment>
                                );
                            })}
                            <TableBody>
                                <TableRow sx={{
                                    position: "sticky",
                                    bottom: 0,
                                    backgroundColor: "#fff",
                                    zIndex: 3,
                                    borderTop: "2px solid #cfcfcf",
                                }}>
                                    {/* 空のセル */}
                                    <TableCell sx={{ backgroundColor: 'white !important', border: 'none !important' }} />
                                    <TableCell sx={{ backgroundColor: 'white !important', border: 'none !important' }} />
                                    {reportOrder.map((repId) => (
                                        <TableCell key={repId}
                                            sx={{ backgroundColor: 'white !important', border: 'none !important', paddingTop: '5px' }} >
                                            {!approvedMap[repId] && (
                                                <>
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '5px' }}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => handleButton_deleteReport(repId)}
                                                        >
                                                            削除
                                                        </Button>
                                                    </Box>
                                                    {authority == 1 && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="primary"
                                                                onClick={() => handleButton_approveReport(repId)}
                                                            >
                                                                承認
                                                            </Button>
                                                        </Box>
                                                    )}
                                                </>
                                            )}
                                        </TableCell>
                                    ))}

                                    <TableCell sx={{ backgroundColor: 'white !important', border: 'none !important' }} />
                                </TableRow>
                            </TableBody>
                        </CustomTable>
                    </TableContainer>
                    {canEdit && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginLeft: '10px',
                            marginBottom: '10px',
                            paddingRight: `${reportOrder.length * 82}px`
                        }}>
                        </div>
                    )}

                </div>
                <CustomSnackBar
                    open={messageState.snackBarTrue}
                    onClose={() => setMessageInfo('snackBarTrue', false)}
                    severity="success"
                    backgroundColor="#2e8b57"
                    message="保存しました"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
                <CustomSnackBar
                    open={messageState.require}
                    onClose={() => setMessageInfo('require', false)}
                    severity="error"
                    backgroundColor="#db7093"
                    message={requireMessage}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
                <CustomSnackBar
                    open={messageState.snackBarDeleteTrue}
                    onClose={() => setMessageInfo('snackBarDeleteTrue', false)}
                    severity="success"
                    backgroundColor="#2e8b57"
                    message="削除しました"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
                <CustomSnackBar
                    open={messageState.snackBarApproveTrue}
                    onClose={() => setMessageInfo('snackBarApproveTrue', false)}
                    severity="success"
                    backgroundColor="#2e8b57"
                    message="承認しました"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                />
            </div>
            {/* ダイアログ表示 */}
            <div>
                <Dialog open={messageState.confirm} onClose={() => setMessageInfo('confirm', false)}>
                    <DialogContent>
                        <DialogContentText>
                            入力データを登録します。よろしいですか？
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMessageInfo('confirm', false)} color="primary">キャンセル</Button>
                        <Button onClick={e => handleButton_saveAction(e)} color="primary" disabled={isLoading}>続行</Button>
                    </DialogActions>
                </Dialog>
            </div>
            <div>
                <Dialog open={messageState.confirmDelete} onClose={() => setMessageInfo('confirmDelete', false)}>
                    <DialogContent>
                        <DialogContentText>
                            削除します。よろしいですか？
                        </DialogContentText>
                        <DialogContentText sx={{ textAlign: 'center', marginTop: '10px' }}>
                            時間帯：{formatTime(reportTimeList[deleteHygieneId]) || ''}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMessageInfo('confirmDelete', false)} color="primary">キャンセル</Button>
                        <Button onClick={e => handleButton_deleteAction(e)} color="primary" disabled={isLoading}>続行</Button>
                    </DialogActions>
                </Dialog>
            </div>
            <div>
                <Dialog open={messageState.confirmApproval} onClose={() => setMessageInfo('confirmApproval', false)}>
                    <DialogContent>
                        <DialogContentText>
                            承認します。よろしいですか？
                        </DialogContentText>
                        <DialogContentText sx={{ textAlign: 'center', marginTop: '10px' }}>
                            時間帯：{formatTime(reportTimeList[approveHygieneId]) || ''}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setMessageInfo('confirmApproval', false)} color="primary">キャンセル</Button>
                        <Button onClick={e => handleButton_approveAction(e)} color="primary" disabled={isLoading}>続行</Button>
                    </DialogActions>
                </Dialog>
            </div>
            {/* 詳細ダイアログ（備考＋画像） */}
            <Dialog open={detailOpen} onClose={handleButton_closeDetail} fullWidth maxWidth="sm">
                <DialogTitle>
                    詳細（{selectedHaccpItem?.name || ""}）
                    {detailReportId && ` [${formatTime(reportTimeList[detailReportId])}]`}
                    {selectedHaccpItem?.text && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            管理基準：  {selectedHaccpItem?.text}
                        </Typography>
                    )}
                </DialogTitle>

                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="備考1"
                            value={detailData?.comments?.[0] || detailData?.comment?.[0] || ""}
                            onChange={(e) => updateDetail(0, e.target.value)}
                            InputProps={{ readOnly: isDetailReadOnly  }}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                        <TextField
                            label="備考2"
                            value={detailData?.comments?.[1] || detailData?.comment?.[1] || ""}
                            onChange={(e) => updateDetail(1, e.target.value)}
                            InputProps={{ readOnly: isDetailReadOnly  }}
                            fullWidth
                            multiline
                            minRows={3}
                        />
                        {canEdit && !isDetailReadOnly  &&(<Button component="label" variant="outlined">
                            画像を追加
                            <input
                                type="file"
                                hidden
                                accept="image/*"
                                multiple
                                onChange={(e) => addImages(e.target.files)}
                            />
                        </Button>
                        )}

                        {/* プレビュー */}
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                            {(detailData?.imagePaths || []).map((img, i) => (
                                <Box key={i} sx={{ position: "relative" }}>
                                    <Box
                                        component="img"
                                        src={`${fileURL}${img.path}`}
                                        alt={img.title}
                                        onClick={() =>handleClick_dialog_imageFile(`${fileURL}${img.path}`)}
                                        sx={{
                                            width: 96,
                                            height: 96,
                                            objectFit: "cover",
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                        }}
                                    />

                                    {!isDetailReadOnly  && (
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="error"
                                            onClick={() => removeImage(i)}
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                minWidth: 0,
                                                px: 1,
                                                py: 0.2,
                                                lineHeight: 1,
                                            }}
                                        >
                                            削除
                                        </Button>
                                    )}
                                </Box>
                            ))}
                        </Stack>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleButton_closeDetail}>閉じる</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={openDialogImageFile} onClose={() => handleClick_dialog_imageFile} maxWidth="lg">
                <img
                    src={openDialogImageFile}
                    style={{ width: "100%", height: "auto" }}
                />
                <DialogActions>
                    <Button
                        color="tertiary"
                        size="large"
                        variant="contained"
                        sx={dialog_button_close}
                        style={{ width: '500px', margin: "0 auto", marginBottom: '2%' }}
                        onClick={() => handleClick_dialog_imageFile_close()}
                        startIcon={<CloseIcon />}
                    >{'閉じる'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
export default CheckList;